import React, { createContext, useContext, useState, useCallback } from 'react';
import ScreenShare from '../shared/ScreenShare';

const ScreenShareContext = createContext(null);

export function ScreenShareProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [sessionData, setSessionData] = useState(null);

  // useCallback prevents unnecessary re-renders
  const openScreenShare = useCallback(({ activeChat, userId, socket }) => {
    setSessionData({ activeChat, userId, socket });
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const closeScreenShare = useCallback(() => {
    setIsOpen(false);
    setIsMinimized(false);
    // Keep session data for a moment to allow cleanup
    setTimeout(() => setSessionData(null), 300);
  }, []);

  const minimizeScreenShare = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const maximizeScreenShare = useCallback(() => {
    setIsMinimized(false);
  }, []);

  return (
    <ScreenShareContext.Provider value={{ 
      openScreenShare, 
      closeScreenShare, 
      minimizeScreenShare,
      maximizeScreenShare,
      isOpen,
      isMinimized 
    }}>
      {children}
      
      {/* Modal popup - no backdrop, stays on screen */}
      {isOpen && sessionData && (
        <>
          {!isMinimized ? (
            // Full screen share window
            <div style={{ 
              position: 'fixed', 
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              maxWidth: '90vw',
              maxHeight: '90vh',
              width: '1200px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: 'white'
            }}>
              <ScreenShare
                activeChat={sessionData.activeChat}
                userId={sessionData.userId}
                socket={sessionData.socket}
                onClose={closeScreenShare}
                onMinimize={minimizeScreenShare}
              />
            </div>
          ) : (
            // Minimized floating button
            <div 
              onClick={maximizeScreenShare}
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 9999,
                backgroundColor: '#9333ea',
                color: 'white',
                padding: '16px 24px',
                borderRadius: '50px',
                boxShadow: '0 10px 25px rgba(147, 51, 234, 0.3)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontWeight: '500',
                transition: 'all 0.2s',
                userSelect: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.backgroundColor = '#7e22ce';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = '#9333ea';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                <line x1="8" y1="21" x2="16" y2="21"></line>
                <line x1="12" y1="17" x2="12" y2="21"></line>
              </svg>
              Screen Share Active
            </div>
          )}
        </>
      )}
    </ScreenShareContext.Provider>
  );
}

// Custom hook for easy access
export function useScreenShare() {
  const context = useContext(ScreenShareContext);
  
  if (!context) {
    throw new Error('useScreenShare must be used within ScreenShareProvider');
  }
  
  return context;
}

// Optional: For backwards compatibility with your existing code
export default ScreenShareContext;